<script setup lang="ts">
import { useMeStore } from '@/stores/me'
import { brokerIsStroobants } from '@/utils/brokers'
import { RequestStatus } from '@/utils/restStore'
import { useI18n } from 'vue-i18n'

import UIIcon from '@/components/generic/atoms/UIIcon.vue'
import Modal from '@/components/generic/molecules/Modal.vue'
import ModalYagoContactScope from '@/components/generic/molecules/ModalYagoContactScope.vue'
import ModalYagoReassuranceScope from '@/components/generic/molecules/ModalYagoReassuranceScope.vue'

import onlineRequest from '@/assets/svg/illustrations/online_request.svg?component'

const meStore = useMeStore()
const { locale } = useI18n()

const properties = defineProps<{
  closeModal: () => void
}>()
</script>

<template>
  <Modal :open="true" :close-button="true" class="xs:max-w-[848px]" @close="properties.closeModal">
    <template v-slot:custom-content>
      <h2 class="font-semibold text-3xl mb-8 mt-4">{{ $t('modals.add_company.title') }}</h2>
      <div class="grid md:grid-cols-2 gap-4 md:mx-2 mb-4" v-if="meStore.me.status === RequestStatus.RequestLoaded">
        <div class="bg-sage-200 rounded-xl p-4 md:p-8">
          <div class="w-24 h-24 mx-auto rounded-full overflow-hidden">
            <img :src="meStore.selectedProfilePca.avatar" alt="advisor" class="object-cover object-center" />
          </div>
          <h3 class="font-semibold text-xl mt-4">{{ $t('modals.add_company.subtitle_1') }}</h3>
          <p class="text-sm mt-1 mb-6">{{ $t('modals.add_company.description_1') }}</p>

          <a :href="$t('external_links.tel', { tel: '+32 2 588 08 24' })">
            <button class="cta mb-2 text-white text-sm font-normal py-3.5 px-6">
              {{ $t('modals.general_wording.contact.cta_tel_label', { tel: '+32 2 588 08 24' }) }}
            </button></a
          >
          <a :href="`mailto:${meStore.selectedProfilePca.email}`">
            <button class="cta text-white mb-6 text-sm font-normal py-3.5 px-6">
              {{ $t('modals.general_wording.contact.cta_mail_label') }}
            </button></a
          >
          <p class="text-sm">{{ $t('modals.general_wording.contact.opening_hours') }}</p>

          <ModalYagoContactScope v-if="locale !== 'en' && !brokerIsStroobants()">
            <template v-slot:book>
              <a :href="$t('external_links.forms.book_a_meeting.phone')" target="_blank">
                <UIIcon route-name="mobile" class="mx-auto mb-2" />{{
                  $t('modals.general_wording.contact.book_a_call_label')
                }}
              </a>
              <a :href="$t('external_links.forms.book_a_meeting.office')" target="_blank">
                <UIIcon route-name="pin" class="mx-auto mb-2" />{{
                  $t('modals.general_wording.contact.book_a_meeting_label')
                }}
              </a>
            </template>
          </ModalYagoContactScope>
        </div>

        <div class="bg-sage-200 rounded-xl p-4 md:p-8">
          <onlineRequest class="mx-auto" />
          <h3 class="font-semibold text-xl mt-3">{{ $t('modals.add_company.subtitle_2') }}</h3>
          <p class="text-sm mt-2">{{ $t('modals.add_company.description_2') }}</p>
          <a
            :href="
              $t('modals.add_company.paperform', {
                firstName: meStore.me.data.firstName,
                lastName: meStore.me.data.lastName,
                email: meStore.me.data.email
              })
            "
            target="_blank">
            <button class="cta text-white my-6 text-sm font-normal py-3.5 px-6">
              {{ $t('modals.add_company.cta_email_label') }}
            </button>
          </a>

          <ModalYagoReassuranceScope v-if="!brokerIsStroobants()" />
        </div>
      </div>
    </template>
  </Modal>
</template>
